import {getImageServerFullUrl, getImageServerSmallUrl, getUserAvatarByName} from "../../actions/environment";
import React, {useRef, useState} from "react";
import LightboxGallery from "./lightbox-gallery";
import {calendarDateFns, formatFnsToNow, useLanguage} from "../../components/configuration/configuration";
import {NavigationGroup, NavigationRow} from "../../components/navigation/layout/nav-layout";
import {RoundedButton, Tooltip} from "../../components/navigation/buttons/nav-buttons";
import {Popover} from "../../components/navigation/popover/popover";
import {
    AddPhotoIcon,
    DeleteIcon,
    EditSquareIcon,
    FavoriteIcon,
    FavoriteIconFilled, fs_bigger, fs_regular
} from "../../components/navigation/icons/nav-icons";
import {useAuthorization} from "../../selectors";
import EditGalleryPopover from "../../components/popover/edit-gallery-popover";
import {Link, useParams} from "react-router-dom";
import {MarkDownText} from "../../components/toggler/markdown-text-toggler";
import SpaceAccessIcon from '../../components/navigation/icons/space-access-icon';
import EditGalleryMediaPopover from "../../components/popover/edit-gallery-image-popover";
import useUpdateMediaLike from "../../components/hooks/use-update-media-like";
import {PENDING_STATUS} from "../../actions";
import {Spinner} from "../../components/util/spinner";

// export const LightboxMedia = {
export class _LightboxMedia {
    constructor(mediaId, url, title, description) {
        this.mediaId = mediaId;
        this.url = url;
        this.title = title;
        this.description = description;
    }
}

// usage:
// const LightboxEntry = objectOf('mediaId, url, title, description');
// const entry = new LightboxMedia(1, 'https://picsum.photos/200/300', 'Title', 'Description');
// no type checking and pass by value utility
// -- put somewhere else --
export function objectOf(keys) {
    if(!keys) return null;
    const k = keys.split(', ');

    function constructor() {
        for(let i=0; i<k.length; i++) this[k[i]] = arguments[i];
    }
    return constructor;
}
export const LightboxEntry = objectOf('mediaId, url, title, description, likes, isLiked');


const LightboxGalleryView = ({pagedData, onDelete, onUpload, onLike, onDeleteGallery}) => {
    const [showMediaGallery, setShowMediaGallery] = useState({isOpen: false, index: 0});
    const lightBoxEntries = useRef([]);
    const lightBoxGalleryRef = useRef(null);
    const {username, spaceId} = useParams();
    const {isAuthorized, principalUsername, isSuperUser} = useAuthorization();
    const [isOwner] = useState(username === principalUsername);
    const [editGalleryForm, setEditGalleryForm] = useState({
        name: pagedData ? pagedData.name : '',
        description: pagedData ? pagedData.description : '',
        access: pagedData ? pagedData.access : '',
    })
    const {updateSpaceMediaLike, media, status, error} = useUpdateMediaLike()

    const Lang = useLanguage();

    const handleOnMediaClick = event => {
        event.preventDefault();
        const idx = event.target.dataset.idx;
        setShowMediaGallery({isOpen: true, index: parseInt(idx)})
    }

    // const handleOnDelete = mediaId => event => {
    const handleOnDeleteMedia = event => {
        event.preventDefault();
        const mediaId = event.target.dataset.mediaId;
        onDelete && onDelete(parseInt(mediaId));
    }

    const renderDeleteIcon = mediaId => {
        return <RoundedButton className="btn-primary delete-icon"
                              data-media-id={mediaId}
                              title={Lang.button.deleteImage.text}
                              onClick={handleOnDeleteMedia}>
            <DeleteIcon size={fs_regular} className="clr-white"/>
        </RoundedButton>
    }

    const renderLikeIcon = (mediaId, likes, isLiked) => {
        const icon = isLiked ? <FavoriteIconFilled className='clr-red'/> :
            <FavoriteIcon white/>
        return <div className='like-icon'>
            <div className='d-flex justify-content-lg-end align-items-lg-center'
                 data-media-id={mediaId}
                 title={Lang.button.likeImage.text}>
                {likes > 0 && <span className='like-text mr-1'>{likes}</span>}
                <span>{icon}</span>
            </div>
        </div>
    }

    // base data type for internal com is LightboxEntry
    const renderGalleryImages = (medialist) => {
        return medialist
        .filter(media => media.type === 'PICTURE')
        .map((media, idx) => {

            const created = calendarDateFns(media.created)
            const updated = media.updated != null ? calendarDateFns(media.updated) : created;

            const thumbnail = getImageServerSmallUrl(media.url);
            const isLiked = media.likes.find(like => like.user.username === principalUsername) != null;
            const entry = new LightboxEntry(media.id, getImageServerFullUrl(media.url),
                media.title,
                media.description,
                media.likes.length,
                isLiked)
            lightBoxEntries.current.push(entry)

            return <div key={idx} className="col">
                <div className='gallery-box'>
                    <a className="gallery-item rounded">
                        <img src={thumbnail} data-idx={idx} className="img-fluid" onClick={handleOnMediaClick}
                             alt={media.title}/>
                    </a>
                    <span
                        className="gallery-title text-truncate">{idx+1} {media.title}
                    </span>
                    {isAuthorized && (isOwner || isSuperUser) && renderDeleteIcon(media.id)}
                    {renderLikeIcon(entry.mediaId, entry.likes, entry.isLiked)}
                </div>
            </div>
        })
    }

    // requires a double update for original paged data and for interim list for lightbox
    const onLikeGalleryImage = (likeData) => {
        updateSpaceMediaLike(spaceId, likeData.mediaId, media => {
            lightBoxEntries.current[showMediaGallery.index].isLiked = likeData.isLiked;
            lightBoxEntries.current[showMediaGallery.index].likes = media.likes.length;

            const idx = pagedData.page.content.findIndex(media => media.id === likeData.mediaId);
            pagedData.page.content[idx] = media;

            lightBoxGalleryRef.current.onLikeDataChange(lightBoxEntries.current[showMediaGallery.index])
        });
    }

    // force a data refresh
    const onUpdateGalleryData = galleryData => {
        setEditGalleryForm(() => {
            return {
                name: galleryData.name,
                access: galleryData.access,
                description: galleryData.description}
            }
        );
    }

    // close is a callback from popover component and it will be magically generated there
    // edit gallery information i.e., name, description, access
    const showEditGalleryPopoverDialog = ({close}) => {
        return <div>
            <EditGalleryPopover spaceId={spaceId}
                                access={editGalleryForm.access}
                                name={editGalleryForm.name}
                                description={editGalleryForm.description}
                                onClose={close}
                                onUpdate={onUpdateGalleryData}/>
        </div>
    }

    // update both the original lightBoxEntries list and the open instance in the lightbox per ref
    // avoid calling the server after the database update for performance
    // there is a threesome data circuit between EditGalleryImagePopover, LightBox and LightBoxGalleryView
    const onUpdateGalleryImageData = imageFormData => {
        lightBoxEntries.current[showMediaGallery.index].title = imageFormData.title;
        lightBoxEntries.current[showMediaGallery.index].description = imageFormData.description;

        lightBoxGalleryRef.current.onImageDataChange({
            imageTitle: imageFormData.title,
            imageCaption: imageFormData.description});
    }

    const onGalleryChange = index => {
        setShowMediaGallery(oldvalue => {return {...oldvalue, index: index}})
    }

    // close is a callback from popover component, and it will be magically generated there
    // edit media information i.e., title and description
    const showEditGalleryMediaPopoverDialog = ({close}) => {
        const currentMedia = lightBoxEntries.current[showMediaGallery.index];
        return <div>
            <EditGalleryMediaPopover spaceId={spaceId}
                                     mediaId={currentMedia.mediaId}
                                  title={currentMedia.title}
                                  description={currentMedia.description}  // get rid of iso-dates
                                  onClose={close}
                                  onUpdate={onUpdateGalleryImageData}/>
        </div>
    }

    const renderOwnerAvatar = () => {
        const {avatar, firstName, lastName, owner} = pagedData;

        const image = getUserAvatarByName(avatar, firstName, lastName);
        return <Link to={`/${owner}/home`} className="link-secondary">
            <span className="mr-2" title={`${firstName} ${lastName}`}>
                <img  className='image-box-smallest-round' src={image}/>
            </span>
            <span>{firstName}</span> <span className="hide-mobile-text">{lastName}</span>
        </Link>
    }

    if (status === PENDING_STATUS) return <Spinner/>
    if (!pagedData) return null;

    return <div className='gallery-view p-2'>
        <NavigationRow>
            <NavigationGroup>
                <div className='box-aligned'>
                    <h5 className="fw-bold">{editGalleryForm.name}</h5>
                </div>
            </NavigationGroup>
            <NavigationGroup>
                {isAuthorized && onUpload && (isOwner || isSuperUser) &&
                    <Tooltip className=' btn btn-overrides box-aligned' title={Lang.gallery.page.upload}
                             onClick={onUpload}>
                        <button className='btn btn-light btn-success'>
                            <AddPhotoIcon size={fs_regular} aria-hidden="true"/>
                            <span className='small hide-mobile-text ml-1'>{Lang.gallery.page.upload}</span>
                        </button>

                    </Tooltip>}
                {isAuthorized && (isOwner || isSuperUser) &&
                    <Tooltip className=' btn btn-overrides box-aligned' title={Lang.gallery.page.edit}>
                        <Popover className='popover-status-container'
                                 placement='bottom'
                                 render={showEditGalleryPopoverDialog}>
                            <button className='btn btn-light'>
                                <EditSquareIcon size={fs_regular} aria-hidden="true"/>
                                <span className='small hide-mobile-text ml-1'>{Lang.gallery.page.edit}</span>
                            </button>
                        </Popover>

                    </Tooltip>}
                {isAuthorized && onUpload && (isOwner || isSuperUser) &&
                    <Tooltip className=' btn btn-overrides box-aligned' title={Lang.gallery.page.delete}
                             onClick={onDeleteGallery}>
                        <button className='btn btn-light'>
                            <DeleteIcon size={fs_bigger} aria-hidden="true"/>
                            <span className='small hide-mobile-text'>{Lang.gallery.page.delete}</span>
                        </button>

                    </Tooltip>}
            </NavigationGroup>
        </NavigationRow>

        <NavigationRow>
            <NavigationGroup>
                <span>{renderOwnerAvatar()}</span>
                <span className='desktop-only-text text-dark ml-1'>
                        {Lang.gallery.headline.updated} {pagedData.updated && formatFnsToNow(pagedData.updated)}
                    / {Lang.gallery.headline.created} {pagedData.created && calendarDateFns(pagedData.created)}
                </span>
                <span className='mobile-only-text text-dark ml-1'>
                        {pagedData.created && formatFnsToNow(pagedData.created)}
                </span>
            </NavigationGroup>
            <NavigationGroup>
                <div className='ml-1'>
                    <span className='desktop-only-text'>
                        <SpaceAccessIcon access={editGalleryForm.access} className="mr-1"/></span>
                    <span className='mobile-only-text'>
                        <SpaceAccessIcon access={editGalleryForm.access} className="ml-1"/></span>
                </div>
            </NavigationGroup>
        </NavigationRow>

        {pagedData.parentId &&
            <NavigationRow>
                <div>
                    <span className="mr-1">{Lang.gallery.headline.mediaSource}:</span>
                    <Link to={pagedData.parentUrl}>{pagedData.parentName}</Link>
                </div>
            </NavigationRow>}

        <div className="description-box my-2">
            <MarkDownText text={editGalleryForm.description}/>
            <div className="mt-1">&nbsp;</div>
        </div>

        <div className="lightbox-gallery-view">
            <section className="photo-gallery">
                <div className="container">
                    <div className="row row-cols-2 row-cols-md-3 row-cols-lg-5 g-4 gallery-grid">
                        {renderGalleryImages(pagedData.page ? pagedData.page.content : pagedData.media)}
                    </div>
                </div>
            </section>

            <footer className="container desktop-only-text">
                <div className="row mt-4 text-center">
                    <div className="col">
                        <h6><span className="text-light">{editGalleryForm.name}</span></h6>
                        <div className="mb-2">
                            {pagedData.parentUrl && <span className="mr-4">
                                <span className="mr-1">{Lang.gallery.headline.mediaSource}:</span>
                                <Link to={pagedData.parentUrl}>
                                    <span className="link-secondary fw-bold">{pagedData.parentName}</span></Link>
                            </span>}
                            <span>{Lang.gallery.headline.updatedBy}: {renderOwnerAvatar(pagedData)}</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>

        {showMediaGallery.isOpen &&
            <LightboxGallery media={lightBoxEntries.current}
                             ref={lightBoxGalleryRef}
                             open={true}
                             index={showMediaGallery.index}
                             onClose={() => setShowMediaGallery({isOpen: false, index: 0})}
                             onLike={isAuthorized ? onLikeGalleryImage : null}
                             onChange={onGalleryChange}
                             onEdit={ (isAuthorized && (isOwner || isSuperUser)) ? showEditGalleryMediaPopoverDialog : null}
            />}
    </div>
}

export default LightboxGalleryView;